import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { 
  ArrowLeft, 
  Users,
  UserPlus,
  Trash2,
  Edit2,
  AlertCircle,
  Plus,
  Banknote,
  Calculator,
  Briefcase,
  BadgeCheck,
  Download,
  Clock,
  CheckCircle2,
  DollarSign,
  FileText,
  Printer
} from "lucide-react";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric' 
  });
};

// Department options
const DEPARTMENTS = ['Production', 'Design', 'Sales', 'Administration', 'Delivery'];

// Employment types
const EMPLOYMENT_TYPES = ['Full-time', 'Part-time', 'Contract'];

export default function Payroll() {
  const navigate = useNavigate();
  const { isOwner, accessToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  
  // Payroll state
  const [employees, setEmployees] = useState([]);
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [payrollSummary, setPayrollSummary] = useState(null);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [showPayrollModal, setShowPayrollModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [editingPayroll, setEditingPayroll] = useState(null);
  const [employeeForm, setEmployeeForm] = useState({
    name: '',
    position: '',
    department: 'Production',
    employment_type: 'Full-time',
    basic_salary: '',
    hourly_rate: '',
    sss_number: '',
    philhealth_number: '',
    pagibig_number: '',
    tin_number: '',
    bank_account: '',
    contact_number: '',
    date_hired: new Date().toISOString().slice(0, 10)
  });
  const [payrollForm, setPayrollForm] = useState({
    employee_id: '',
    pay_period_start: '',
    pay_period_end: '',
    days_worked: '',
    hours_worked: '',
    overtime_hours: '',
    overtime_rate: '1.25',
    basic_pay: '',
    overtime_pay: '',
    allowances: '',
    bonuses: '',
    sss_contribution: '',
    philhealth_contribution: '',
    pagibig_contribution: '',
    tax_withheld: '',
    other_deductions: '',
    deduction_notes: '',
    notes: ''
  });
  const [deleteEmployeeConfirm, setDeleteEmployeeConfirm] = useState(null);
  const [deletePayrollConfirm, setDeletePayrollConfirm] = useState(null);
  const [showPayslipModal, setShowPayslipModal] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [companySettings, setCompanySettings] = useState(null);
  const payslipRef = useRef(null);
  
  // Batch payslip state
  const [selectedPayrollIds, setSelectedPayrollIds] = useState([]);
  const [generatingBatchPdf, setGeneratingBatchPdf] = useState(false);

  // Auth headers helper
  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${accessToken}` }
  });

  // Fetch company settings for payslip
  const fetchCompanySettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/minimal`, getAuthHeaders());
      setCompanySettings(response.data);
    } catch (error) {
      console.error("Error fetching company settings:", error);
    }
  };

  // Redirect non-admin users
  useEffect(() => {
    if (!isOwner) {
      toast.error("Access denied. Owner access only.");
      navigate("/");
    }
  }, [isOwner, navigate]);

  useEffect(() => {
    if (isOwner) {
      fetchEmployees();
      fetchPayroll();
      fetchCompanySettings();
    }
  }, [isOwner, selectedMonth]);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`, getAuthHeaders());
      setEmployees(response.data || []);
    } catch (error) {
      console.error("Error fetching employees:", error);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPayroll = async () => {
    try {
      const [recordsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/payroll?month=${selectedMonth}`, getAuthHeaders()),
        axios.get(`${API}/payroll/summary/monthly?month=${selectedMonth}`, getAuthHeaders())
      ]);
      setPayrollRecords(recordsRes.data || []);
      setPayrollSummary(summaryRes.data || null);
    } catch (error) {
      console.error("Error fetching payroll:", error);
      setPayrollRecords([]);
      setPayrollSummary(null);
    }
  };

  // Employee handlers
  const handleAddEmployee = async () => {
    if (!employeeForm.name || !employeeForm.position || !employeeForm.basic_salary) {
      toast.error("Please fill in name, position, and basic salary");
      return;
    }

    try {
      const employeeData = {
        ...employeeForm,
        basic_salary: parseFloat(employeeForm.basic_salary) || 0,
        hourly_rate: employeeForm.hourly_rate ? parseFloat(employeeForm.hourly_rate) : null
      };

      if (editingEmployee) {
        await axios.put(`${API}/employees/${editingEmployee.id}`, employeeData, getAuthHeaders());
        toast.success("Employee updated successfully");
      } else {
        await axios.post(`${API}/employees`, employeeData, getAuthHeaders());
        toast.success("Employee added successfully");
      }

      setShowEmployeeModal(false);
      setEditingEmployee(null);
      resetEmployeeForm();
      fetchEmployees();
    } catch (error) {
      console.error("Error saving employee:", error);
      toast.error("Failed to save employee");
    }
  };

  const resetEmployeeForm = () => {
    setEmployeeForm({
      name: '',
      position: '',
      department: 'Production',
      employment_type: 'Full-time',
      basic_salary: '',
      hourly_rate: '',
      sss_number: '',
      philhealth_number: '',
      pagibig_number: '',
      tin_number: '',
      bank_account: '',
      contact_number: '',
      date_hired: new Date().toISOString().slice(0, 10)
    });
  };

  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee);
    setEmployeeForm({
      name: employee.name || '',
      position: employee.position || '',
      department: employee.department || 'Production',
      employment_type: employee.employment_type || 'Full-time',
      basic_salary: employee.basic_salary?.toString() || '',
      hourly_rate: employee.hourly_rate?.toString() || '',
      sss_number: employee.sss_number || '',
      philhealth_number: employee.philhealth_number || '',
      pagibig_number: employee.pagibig_number || '',
      tin_number: employee.tin_number || '',
      bank_account: employee.bank_account || '',
      contact_number: employee.contact_number || '',
      date_hired: employee.date_hired || new Date().toISOString().slice(0, 10)
    });
    setShowEmployeeModal(true);
  };

  const handleDeleteEmployee = async (employeeId) => {
    try {
      await axios.delete(`${API}/employees/${employeeId}`, getAuthHeaders());
      toast.success("Employee removed successfully");
      setDeleteEmployeeConfirm(null);
      fetchEmployees();
    } catch (error) {
      console.error("Error deleting employee:", error);
      toast.error("Failed to remove employee");
    }
  };

  // Payroll handlers
  const handleAddPayroll = async () => {
    if (!payrollForm.employee_id || !payrollForm.pay_period_start || !payrollForm.pay_period_end) {
      toast.error("Please select employee and pay period");
      return;
    }

    try {
      const payrollData = {
        ...payrollForm,
        days_worked: parseFloat(payrollForm.days_worked) || 0,
        hours_worked: parseFloat(payrollForm.hours_worked) || 0,
        overtime_hours: parseFloat(payrollForm.overtime_hours) || 0,
        overtime_rate: parseFloat(payrollForm.overtime_rate) || 1.25,
        basic_pay: parseFloat(payrollForm.basic_pay) || 0,
        overtime_pay: parseFloat(payrollForm.overtime_pay) || 0,
        allowances: parseFloat(payrollForm.allowances) || 0,
        bonuses: parseFloat(payrollForm.bonuses) || 0,
        sss_contribution: parseFloat(payrollForm.sss_contribution) || 0,
        philhealth_contribution: parseFloat(payrollForm.philhealth_contribution) || 0,
        pagibig_contribution: parseFloat(payrollForm.pagibig_contribution) || 0,
        tax_withheld: parseFloat(payrollForm.tax_withheld) || 0,
        other_deductions: parseFloat(payrollForm.other_deductions) || 0
      };

      if (editingPayroll) {
        await axios.put(`${API}/payroll/${editingPayroll.id}`, payrollData, getAuthHeaders());
        toast.success("Payroll record updated successfully");
      } else {
        await axios.post(`${API}/payroll`, payrollData, getAuthHeaders());
        toast.success("Payroll record created successfully");
      }

      setShowPayrollModal(false);
      setEditingPayroll(null);
      resetPayrollForm();
      fetchPayroll();
    } catch (error) {
      console.error("Error saving payroll:", error);
      toast.error("Failed to save payroll record");
    }
  };

  const resetPayrollForm = () => {
    setPayrollForm({
      employee_id: '',
      pay_period_start: '',
      pay_period_end: '',
      days_worked: '',
      hours_worked: '',
      overtime_hours: '',
      overtime_rate: '1.25',
      basic_pay: '',
      overtime_pay: '',
      allowances: '',
      bonuses: '',
      sss_contribution: '',
      philhealth_contribution: '',
      pagibig_contribution: '',
      tax_withheld: '',
      other_deductions: '',
      deduction_notes: '',
      notes: ''
    });
  };

  const handleEditPayroll = (payroll) => {
    setEditingPayroll(payroll);
    setPayrollForm({
      employee_id: payroll.employee_id || '',
      pay_period_start: payroll.pay_period_start || '',
      pay_period_end: payroll.pay_period_end || '',
      days_worked: payroll.days_worked?.toString() || '',
      hours_worked: payroll.hours_worked?.toString() || '',
      overtime_hours: payroll.overtime_hours?.toString() || '',
      overtime_rate: payroll.overtime_rate?.toString() || '1.25',
      basic_pay: payroll.basic_pay?.toString() || '',
      overtime_pay: payroll.overtime_pay?.toString() || '',
      allowances: payroll.allowances?.toString() || '',
      bonuses: payroll.bonuses?.toString() || '',
      sss_contribution: payroll.sss_contribution?.toString() || '',
      philhealth_contribution: payroll.philhealth_contribution?.toString() || '',
      pagibig_contribution: payroll.pagibig_contribution?.toString() || '',
      tax_withheld: payroll.tax_withheld?.toString() || '',
      other_deductions: payroll.other_deductions?.toString() || '',
      deduction_notes: payroll.deduction_notes || '',
      notes: payroll.notes || ''
    });
    setShowPayrollModal(true);
  };

  const handleDeletePayroll = async (payrollId) => {
    try {
      await axios.delete(`${API}/payroll/${payrollId}`, getAuthHeaders());
      toast.success("Payroll record deleted successfully");
      setDeletePayrollConfirm(null);
      fetchPayroll();
    } catch (error) {
      console.error("Error deleting payroll:", error);
      toast.error(error.response?.data?.detail || "Failed to delete payroll record");
    }
  };

  const handleUpdatePayrollStatus = async (payrollId, newStatus) => {
    try {
      await axios.patch(`${API}/payroll/${payrollId}/status?status=${newStatus}`, {}, getAuthHeaders());
      toast.success(`Payroll marked as ${newStatus}`);
      fetchPayroll();
    } catch (error) {
      console.error("Error updating payroll status:", error);
      toast.error("Failed to update status");
    }
  };

  // Open payslip modal
  const handleViewPayslip = (record) => {
    const employee = employees.find(e => e.id === record.employee_id);
    setSelectedPayslip({
      ...record,
      employee_details: employee || {}
    });
    setShowPayslipModal(true);
  };

  // Download payslip as image
  const handleDownloadPayslip = async () => {
    if (!payslipRef.current) return;
    
    try {
      toast.loading("Generating payslip...");
      const canvas = await html2canvas(payslipRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff'
      });
      
      const link = document.createElement('a');
      link.download = `payslip_${selectedPayslip?.employee_name?.replace(/\s+/g, '_')}_${selectedPayslip?.pay_period_start}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.dismiss();
      toast.success("Payslip downloaded successfully");
    } catch (error) {
      console.error("Error generating payslip:", error);
      toast.dismiss();
      toast.error("Failed to generate payslip");
    }
  };

  // Print payslip
  const handlePrintPayslip = () => {
    const printContent = payslipRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Payslip - ${selectedPayslip?.employee_name}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
            .payslip-container { max-width: 800px; margin: 0 auto; }
            table { width: 100%; border-collapse: collapse; margin: 10px 0; }
            th, td { padding: 8px 12px; text-align: left; border-bottom: 1px solid #e5e5e5; }
            th { background-color: #f5f5f5; font-weight: 600; }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            .font-bold { font-weight: bold; }
            .text-green { color: #16a34a; }
            .text-red { color: #dc2626; }
            .header { text-align: center; margin-bottom: 20px; padding-bottom: 20px; border-bottom: 2px solid #333; }
            .header h1 { margin: 0 0 5px 0; font-size: 24px; }
            .header p { margin: 0; color: #666; }
            .section-title { background-color: #f0f9ff; padding: 8px 12px; font-weight: bold; margin-top: 15px; }
            .total-row { background-color: #f5f5f5; font-weight: bold; }
            .net-pay { background-color: #dcfce7; font-size: 18px; }
            @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  // Toggle payroll selection for batch
  const togglePayrollSelection = (recordId) => {
    setSelectedPayrollIds(prev => 
      prev.includes(recordId) 
        ? prev.filter(id => id !== recordId)
        : [...prev, recordId]
    );
  };

  // Select all payroll records
  const toggleSelectAll = () => {
    if (selectedPayrollIds.length === payrollRecords.length) {
      setSelectedPayrollIds([]);
    } else {
      setSelectedPayrollIds(payrollRecords.map(r => r.id));
    }
  };

  // Generate batch payslips as single PDF
  const handleBatchPayslipGeneration = async () => {
    if (selectedPayrollIds.length === 0) {
      toast.error("Please select at least one payroll record");
      return;
    }

    setGeneratingBatchPdf(true);
    toast.loading("Generating batch payslips PDF...");

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      
      const selectedRecords = payrollRecords.filter(r => selectedPayrollIds.includes(r.id));
      
      for (let i = 0; i < selectedRecords.length; i++) {
        const record = selectedRecords[i];
        const employee = employees.find(e => e.id === record.employee_id) || {};
        
        if (i > 0) {
          pdf.addPage();
        }
        
        let yPos = margin;
        
        // Company Header
        pdf.setFontSize(18);
        pdf.setFont('helvetica', 'bold');
        pdf.text(companySettings?.business_name || 'Company Name', pageWidth / 2, yPos, { align: 'center' });
        yPos += 7;
        
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text(companySettings?.address || '', pageWidth / 2, yPos, { align: 'center' });
        yPos += 5;
        pdf.text(companySettings?.contact_phone || '', pageWidth / 2, yPos, { align: 'center' });
        yPos += 10;
        
        // Payslip Title
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('PAYSLIP', pageWidth / 2, yPos, { align: 'center' });
        yPos += 10;
        
        // Employee Info
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Employee: ${record.employee_name}`, margin, yPos);
        pdf.text(`Position: ${record.employee_position || employee.position || 'N/A'}`, pageWidth / 2, yPos);
        yPos += 6;
        pdf.text(`Pay Period: ${formatDate(record.pay_period_start)} - ${formatDate(record.pay_period_end)}`, margin, yPos);
        pdf.text(`Status: ${record.status}`, pageWidth / 2, yPos);
        yPos += 10;
        
        // Earnings Section
        pdf.setFillColor(240, 249, 255);
        pdf.rect(margin, yPos, pageWidth - (margin * 2), 8, 'F');
        pdf.setFont('helvetica', 'bold');
        pdf.text('EARNINGS', margin + 2, yPos + 5);
        yPos += 12;
        
        pdf.setFont('helvetica', 'normal');
        const earnings = [
          ['Basic Pay', formatCurrency(record.basic_pay)],
          ['Overtime Pay', formatCurrency(record.overtime_pay || 0)],
          ['Allowances', formatCurrency(record.allowances || 0)],
          ['Bonuses', formatCurrency(record.bonuses || 0)],
        ];
        
        earnings.forEach(([label, value]) => {
          pdf.text(label, margin + 5, yPos);
          pdf.text(value, pageWidth - margin - 5, yPos, { align: 'right' });
          yPos += 5;
        });
        
        pdf.setFont('helvetica', 'bold');
        pdf.text('GROSS PAY', margin + 5, yPos);
        pdf.text(formatCurrency(record.gross_pay), pageWidth - margin - 5, yPos, { align: 'right' });
        yPos += 10;
        
        // Deductions Section
        pdf.setFillColor(254, 242, 242);
        pdf.rect(margin, yPos, pageWidth - (margin * 2), 8, 'F');
        pdf.text('DEDUCTIONS', margin + 2, yPos + 5);
        yPos += 12;
        
        pdf.setFont('helvetica', 'normal');
        const deductions = [
          ['SSS', formatCurrency(record.sss_contribution || 0)],
          ['PhilHealth', formatCurrency(record.philhealth_contribution || 0)],
          ['Pag-IBIG', formatCurrency(record.pagibig_contribution || 0)],
          ['Withholding Tax', formatCurrency(record.tax_withheld || 0)],
          ['Other Deductions', formatCurrency(record.other_deductions || 0)],
        ];
        
        deductions.forEach(([label, value]) => {
          pdf.text(label, margin + 5, yPos);
          pdf.text(value, pageWidth - margin - 5, yPos, { align: 'right' });
          yPos += 5;
        });
        
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(220, 38, 38);
        pdf.text('TOTAL DEDUCTIONS', margin + 5, yPos);
        pdf.text(formatCurrency(record.total_deductions), pageWidth - margin - 5, yPos, { align: 'right' });
        yPos += 10;
        
        // Net Pay
        pdf.setTextColor(0, 0, 0);
        pdf.setFillColor(220, 252, 231);
        pdf.rect(margin, yPos, pageWidth - (margin * 2), 12, 'F');
        pdf.setFontSize(12);
        pdf.text('NET PAY', margin + 5, yPos + 8);
        pdf.setTextColor(22, 163, 74);
        pdf.text(formatCurrency(record.net_pay), pageWidth - margin - 5, yPos + 8, { align: 'right' });
        yPos += 20;
        
        // Footer
        pdf.setTextColor(128, 128, 128);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.text('This is a computer-generated payslip. No signature required.', pageWidth / 2, pageHeight - 15, { align: 'center' });
        pdf.text(`Generated on ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
      }
      
      // Save the PDF
      pdf.save(`batch_payslips_${selectedMonth}.pdf`);
      
      toast.dismiss();
      toast.success(`Successfully generated ${selectedRecords.length} payslips`);
      setSelectedPayrollIds([]);
    } catch (error) {
      console.error("Error generating batch payslips:", error);
      toast.dismiss();
      toast.error("Failed to generate batch payslips");
    } finally {
      setGeneratingBatchPdf(false);
    }
  };

  // Calculate payroll when employee is selected
  const calculatePayroll = (employeeId) => {
    const employee = employees.find(e => e.id === employeeId);
    if (employee) {
      const basicPay = employee.basic_salary || 0;
      // Standard Philippine deductions (simplified)
      const sss = Math.min(basicPay * 0.045, 1350); // 4.5% employee share, max ~1350
      const philhealth = basicPay * 0.025; // 2.5% employee share
      const pagibig = Math.min(basicPay * 0.02, 100); // 2%, max 100
      
      setPayrollForm(prev => ({
        ...prev,
        basic_pay: basicPay.toString(),
        sss_contribution: sss.toFixed(2),
        philhealth_contribution: philhealth.toFixed(2),
        pagibig_contribution: pagibig.toFixed(2)
      }));
    }
  };

  const exportPayrollReport = () => {
    if (!payrollRecords.length) {
      toast.error("No payroll records to export");
      return;
    }

    const reportData = [
      ["Payroll Report", selectedMonth],
      [""],
      ["Summary"],
      ["Total Gross Pay", formatCurrency(payrollSummary?.total_gross_pay || 0)],
      ["Total Deductions", formatCurrency(payrollSummary?.total_deductions || 0)],
      ["Total Net Pay", formatCurrency(payrollSummary?.total_net_pay || 0)],
      [""],
      ["Deductions Breakdown"],
      ["SSS", formatCurrency(payrollSummary?.breakdown?.sss || 0)],
      ["PhilHealth", formatCurrency(payrollSummary?.breakdown?.philhealth || 0)],
      ["Pag-IBIG", formatCurrency(payrollSummary?.breakdown?.pagibig || 0)],
      ["Tax Withheld", formatCurrency(payrollSummary?.breakdown?.tax || 0)],
      [""],
      ["Payroll Details"],
      ["Employee", "Period Start", "Period End", "Gross Pay", "Deductions", "Net Pay", "Status"],
      ...payrollRecords.map(r => [
        r.employee_name,
        r.pay_period_start,
        r.pay_period_end,
        formatCurrency(r.gross_pay),
        formatCurrency(r.total_deductions),
        formatCurrency(r.net_pay),
        r.status
      ])
    ];

    const csvContent = reportData.map(row => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payroll_report_${selectedMonth}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Payroll report exported successfully");
  };

  // Generate month options
  const getMonthOptions = () => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push(date.toISOString().slice(0, 7));
    }
    return months;
  };

  if (!isOwner) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <header className="bg-white/80 backdrop-blur-md border-b border-border sticky top-0 z-40">
          <div className="container mx-auto px-6 py-4">
            <Skeleton className="h-8 w-64" />
          </div>
        </header>
        <main className="container mx-auto px-6 py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-border sticky top-0 z-40">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate("/")}
                data-testid="back-button"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-extrabold tracking-tight text-primary">
                  Payroll Management
                </h1>
                <p className="text-sm text-muted-foreground">Employee records and salary processing</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-40" data-testid="month-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getMonthOptions().map(month => (
                    <SelectItem key={month} value={month}>
                      {new Date(month + "-01").toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={exportPayrollReport} variant="outline" data-testid="export-payroll">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card data-testid="total-employees-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Employees</CardTitle>
              <Users className="h-5 w-5 text-blue-600" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-blue-600">{employees.length}</p>
              <p className="text-xs text-muted-foreground mt-1">{employees.filter(e => e.is_active).length} active</p>
            </CardContent>
          </Card>

          <Card data-testid="gross-payroll-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Gross Payroll</CardTitle>
              <DollarSign className="h-5 w-5 text-green-600" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(payrollSummary?.total_gross_pay || 0)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">{payrollSummary?.total_records || 0} records</p>
            </CardContent>
          </Card>

          <Card data-testid="deductions-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Deductions</CardTitle>
              <Calculator className="h-5 w-5 text-red-600" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-red-600">
                {formatCurrency(payrollSummary?.total_deductions || 0)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">SSS, PhilHealth, Pag-IBIG, Tax</p>
            </CardContent>
          </Card>

          <Card data-testid="net-payroll-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Net Payroll</CardTitle>
              <Banknote className="h-5 w-5 text-emerald-600" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-emerald-600">
                {formatCurrency(payrollSummary?.total_net_pay || 0)}
              </p>
              <div className="text-xs text-muted-foreground mt-1 flex gap-2">
                <span className="text-amber-600">{payrollSummary?.status_counts?.pending || 0} pending</span>
                <span className="text-green-600">{payrollSummary?.status_counts?.paid || 0} paid</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="employees" className="space-y-6">
          <TabsList>
            <TabsTrigger value="employees" data-testid="tab-employees">Employees</TabsTrigger>
            <TabsTrigger value="payroll-records" data-testid="tab-payroll-records">Payroll Records</TabsTrigger>
            <TabsTrigger value="deductions" data-testid="tab-deductions">Deductions Summary</TabsTrigger>
          </TabsList>

          {/* Employees Tab */}
          <TabsContent value="employees">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-blue-600" />
                    Employee Directory
                  </CardTitle>
                  <Button 
                    onClick={() => {
                      setEditingEmployee(null);
                      resetEmployeeForm();
                      setShowEmployeeModal(true);
                    }}
                    data-testid="add-employee-btn"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add Employee
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {employees.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Users className="h-16 w-16 mx-auto mb-4 opacity-50" />
                    <p className="text-lg">No employees added yet</p>
                    <p className="text-sm">Click "Add Employee" to get started</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-secondary">
                        <tr>
                          <th className="text-left p-3">Name</th>
                          <th className="text-left p-3">Position</th>
                          <th className="text-left p-3">Department</th>
                          <th className="text-left p-3">Type</th>
                          <th className="text-right p-3">Salary</th>
                          <th className="text-center p-3">Status</th>
                          <th className="text-center p-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {employees.map(employee => (
                          <tr key={employee.id} className={`border-t border-border hover:bg-secondary/50 ${!employee.is_active ? 'opacity-60' : ''}`}>
                            <td className="p-3 font-medium">{employee.name}</td>
                            <td className="p-3">{employee.position}</td>
                            <td className="p-3">{employee.department}</td>
                            <td className="p-3">
                              <span className={`text-xs px-2 py-1 rounded ${
                                employee.employment_type === 'Full-time' ? 'bg-blue-100 text-blue-700' :
                                employee.employment_type === 'Part-time' ? 'bg-amber-100 text-amber-700' :
                                'bg-purple-100 text-purple-700'
                              }`}>
                                {employee.employment_type}
                              </span>
                            </td>
                            <td className="p-3 text-right font-semibold">{formatCurrency(employee.basic_salary)}</td>
                            <td className="p-3 text-center">
                              <span className={`text-xs px-2 py-1 rounded ${
                                employee.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                              }`}>
                                {employee.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="p-3 text-center">
                              <div className="flex justify-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditEmployee(employee)}
                                  data-testid={`edit-employee-${employee.id}`}
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => setDeleteEmployeeConfirm(employee)}
                                  data-testid={`delete-employee-${employee.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payroll Records Tab */}
          <TabsContent value="payroll-records">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-green-600" />
                    Payroll Records - {new Date(selectedMonth + "-01").toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </CardTitle>
                  <div className="flex gap-2">
                    {selectedPayrollIds.length > 0 && (
                      <Button
                        onClick={handleBatchPayslipGeneration}
                        variant="outline"
                        disabled={generatingBatchPdf}
                        data-testid="batch-payslip-btn"
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Generate {selectedPayrollIds.length} Payslip{selectedPayrollIds.length > 1 ? 's' : ''} (PDF)
                      </Button>
                    )}
                    <Button 
                      onClick={() => {
                        setEditingPayroll(null);
                        resetPayrollForm();
                        // Set default pay period to current semi-monthly
                        const now = new Date();
                        const day = now.getDate();
                        if (day <= 15) {
                          setPayrollForm(prev => ({
                            ...prev,
                            pay_period_start: `${selectedMonth}-01`,
                            pay_period_end: `${selectedMonth}-15`
                          }));
                        } else {
                          setPayrollForm(prev => ({
                            ...prev,
                            pay_period_start: `${selectedMonth}-16`,
                            pay_period_end: `${selectedMonth}-${new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()}`
                          }));
                        }
                        setShowPayrollModal(true);
                      }}
                      data-testid="add-payroll-btn"
                      disabled={employees.filter(e => e.is_active).length === 0}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      New Payroll
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {payrollRecords.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Briefcase className="h-16 w-16 mx-auto mb-4 opacity-50" />
                    <p className="text-lg">No payroll records for this month</p>
                    {employees.filter(e => e.is_active).length === 0 && (
                      <p className="text-sm">Add employees first to create payroll</p>
                    )}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-secondary">
                        <tr>
                          <th className="text-center p-3 w-10">
                            <input
                              type="checkbox"
                              checked={selectedPayrollIds.length === payrollRecords.length && payrollRecords.length > 0}
                              onChange={toggleSelectAll}
                              className="h-4 w-4 rounded border-gray-300 cursor-pointer"
                              data-testid="select-all-payroll"
                            />
                          </th>
                          <th className="text-left p-3">Employee</th>
                          <th className="text-left p-3">Pay Period</th>
                          <th className="text-right p-3">Gross Pay</th>
                          <th className="text-right p-3">Deductions</th>
                          <th className="text-right p-3">Net Pay</th>
                          <th className="text-center p-3">Status</th>
                          <th className="text-center p-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payrollRecords.map(record => (
                          <tr key={record.id} className="border-t border-border hover:bg-secondary/50">
                            <td className="p-3 text-center">
                              <input
                                type="checkbox"
                                checked={selectedPayrollIds.includes(record.id)}
                                onChange={() => togglePayrollSelection(record.id)}
                                className="h-4 w-4 rounded border-gray-300 cursor-pointer"
                                data-testid={`select-payroll-${record.id}`}
                              />
                            </td>
                            <td className="p-3">
                              <p className="font-medium">{record.employee_name}</p>
                              <p className="text-xs text-muted-foreground">{record.employee_position}</p>
                            </td>
                            <td className="p-3">
                              <p className="text-sm">{formatDate(record.pay_period_start)}</p>
                              <p className="text-xs text-muted-foreground">to {formatDate(record.pay_period_end)}</p>
                            </td>
                            <td className="p-3 text-right font-semibold">{formatCurrency(record.gross_pay)}</td>
                            <td className="p-3 text-right text-red-600">{formatCurrency(record.total_deductions)}</td>
                            <td className="p-3 text-right font-bold text-green-600">{formatCurrency(record.net_pay)}</td>
                            <td className="p-3 text-center">
                              <span className={`text-xs px-2 py-1 rounded ${
                                record.status === 'Paid' ? 'bg-green-100 text-green-700' :
                                record.status === 'Approved' ? 'bg-blue-100 text-blue-700' :
                                'bg-amber-100 text-amber-700'
                              }`}>
                                {record.status}
                              </span>
                            </td>
                            <td className="p-3 text-center">
                              <div className="flex justify-center gap-1">
                                {record.status === 'Pending' && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-blue-600 h-8 px-2"
                                    onClick={() => handleUpdatePayrollStatus(record.id, 'Approved')}
                                    title="Approve"
                                  >
                                    <BadgeCheck className="h-4 w-4" />
                                  </Button>
                                )}
                                {record.status === 'Approved' && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-green-600 h-8 px-2"
                                    onClick={() => handleUpdatePayrollStatus(record.id, 'Paid')}
                                    title="Mark as Paid"
                                  >
                                    <CheckCircle2 className="h-4 w-4" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-blue-600 h-8 px-2"
                                  onClick={() => handleViewPayslip(record)}
                                  title="View Payslip"
                                >
                                  <FileText className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 px-2"
                                  onClick={() => handleEditPayroll(record)}
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                {record.status !== 'Paid' && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-600 h-8 px-2"
                                    onClick={() => setDeletePayrollConfirm(record)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-secondary font-semibold">
                        <tr>
                          <td colSpan="2" className="p-3 text-right">Totals:</td>
                          <td className="p-3 text-right">{formatCurrency(payrollSummary?.total_gross_pay)}</td>
                          <td className="p-3 text-right text-red-600">{formatCurrency(payrollSummary?.total_deductions)}</td>
                          <td className="p-3 text-right text-green-600">{formatCurrency(payrollSummary?.total_net_pay)}</td>
                          <td colSpan="2"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Deductions Summary Tab */}
          <TabsContent value="deductions">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calculator className="h-5 w-5 text-purple-600" />
                    Government Contributions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-4 bg-blue-50 rounded-lg flex justify-between items-center">
                      <div>
                        <p className="font-semibold text-blue-700">SSS Contributions</p>
                        <p className="text-xs text-muted-foreground">Social Security System</p>
                      </div>
                      <p className="text-2xl font-bold text-blue-700">{formatCurrency(payrollSummary?.breakdown?.sss || 0)}</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg flex justify-between items-center">
                      <div>
                        <p className="font-semibold text-green-700">PhilHealth</p>
                        <p className="text-xs text-muted-foreground">Philippine Health Insurance</p>
                      </div>
                      <p className="text-2xl font-bold text-green-700">{formatCurrency(payrollSummary?.breakdown?.philhealth || 0)}</p>
                    </div>
                    <div className="p-4 bg-amber-50 rounded-lg flex justify-between items-center">
                      <div>
                        <p className="font-semibold text-amber-700">Pag-IBIG</p>
                        <p className="text-xs text-muted-foreground">Home Development Mutual Fund</p>
                      </div>
                      <p className="text-2xl font-bold text-amber-700">{formatCurrency(payrollSummary?.breakdown?.pagibig || 0)}</p>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg flex justify-between items-center">
                      <div>
                        <p className="font-semibold text-red-700">Withholding Tax</p>
                        <p className="text-xs text-muted-foreground">Income tax withheld</p>
                      </div>
                      <p className="text-2xl font-bold text-red-700">{formatCurrency(payrollSummary?.breakdown?.tax || 0)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-amber-600" />
                    Payroll Status Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-4 bg-amber-50 rounded-lg flex justify-between items-center">
                      <div>
                        <p className="font-semibold text-amber-700">Pending</p>
                        <p className="text-xs text-muted-foreground">Awaiting approval</p>
                      </div>
                      <p className="text-2xl font-bold text-amber-700">{payrollSummary?.status_counts?.pending || 0}</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg flex justify-between items-center">
                      <div>
                        <p className="font-semibold text-blue-700">Approved</p>
                        <p className="text-xs text-muted-foreground">Ready for payment</p>
                      </div>
                      <p className="text-2xl font-bold text-blue-700">{payrollSummary?.status_counts?.approved || 0}</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg flex justify-between items-center">
                      <div>
                        <p className="font-semibold text-green-700">Paid</p>
                        <p className="text-xs text-muted-foreground">Payment completed</p>
                      </div>
                      <p className="text-2xl font-bold text-green-700">{payrollSummary?.status_counts?.paid || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Add/Edit Employee Modal */}
      <Dialog open={showEmployeeModal} onOpenChange={setShowEmployeeModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEmployee ? 'Edit Employee' : 'Add New Employee'}</DialogTitle>
            <DialogDescription>
              {editingEmployee ? 'Update employee information.' : 'Add a new employee to the payroll system.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="emp-name">Full Name *</Label>
                <Input
                  id="emp-name"
                  placeholder="Juan Dela Cruz"
                  value={employeeForm.name}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, name: e.target.value })}
                  data-testid="employee-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="emp-position">Position *</Label>
                <Input
                  id="emp-position"
                  placeholder="e.g., Production Staff"
                  value={employeeForm.position}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, position: e.target.value })}
                  data-testid="employee-position-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Department</Label>
                <Select 
                  value={employeeForm.department} 
                  onValueChange={(value) => setEmployeeForm({ ...employeeForm, department: value })}
                >
                  <SelectTrigger data-testid="employee-department-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DEPARTMENTS.map(dept => (
                      <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Employment Type</Label>
                <Select 
                  value={employeeForm.employment_type} 
                  onValueChange={(value) => setEmployeeForm({ ...employeeForm, employment_type: value })}
                >
                  <SelectTrigger data-testid="employee-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EMPLOYMENT_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="emp-salary">Basic Salary (Monthly) *</Label>
                <Input
                  id="emp-salary"
                  type="number"
                  placeholder="0.00"
                  value={employeeForm.basic_salary}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, basic_salary: e.target.value })}
                  data-testid="employee-salary-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="emp-hourly">Hourly Rate (Optional)</Label>
                <Input
                  id="emp-hourly"
                  type="number"
                  placeholder="0.00"
                  value={employeeForm.hourly_rate}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, hourly_rate: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="emp-hired">Date Hired</Label>
              <Input
                id="emp-hired"
                type="date"
                value={employeeForm.date_hired}
                onChange={(e) => setEmployeeForm({ ...employeeForm, date_hired: e.target.value })}
              />
            </div>
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-muted-foreground mb-3">Government IDs</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="emp-sss">SSS Number</Label>
                  <Input
                    id="emp-sss"
                    placeholder="00-0000000-0"
                    value={employeeForm.sss_number}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, sss_number: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="emp-philhealth">PhilHealth Number</Label>
                  <Input
                    id="emp-philhealth"
                    placeholder="00-000000000-0"
                    value={employeeForm.philhealth_number}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, philhealth_number: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="emp-pagibig">Pag-IBIG Number</Label>
                  <Input
                    id="emp-pagibig"
                    placeholder="0000-0000-0000"
                    value={employeeForm.pagibig_number}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, pagibig_number: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="emp-tin">TIN</Label>
                  <Input
                    id="emp-tin"
                    placeholder="000-000-000-000"
                    value={employeeForm.tin_number}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, tin_number: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="emp-bank">Bank Account</Label>
                <Input
                  id="emp-bank"
                  placeholder="Account number"
                  value={employeeForm.bank_account}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, bank_account: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="emp-contact">Contact Number</Label>
                <Input
                  id="emp-contact"
                  placeholder="09XX XXX XXXX"
                  value={employeeForm.contact_number}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, contact_number: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmployeeModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddEmployee} data-testid="save-employee-btn">
              {editingEmployee ? 'Update' : 'Add'} Employee
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Payroll Modal */}
      <Dialog open={showPayrollModal} onOpenChange={setShowPayrollModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPayroll ? 'Edit Payroll Record' : 'New Payroll Record'}</DialogTitle>
            <DialogDescription>
              {editingPayroll ? 'Update payroll details.' : 'Create a payroll record for an employee.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2 col-span-3">
                <Label>Employee *</Label>
                <Select 
                  value={payrollForm.employee_id} 
                  onValueChange={(value) => {
                    setPayrollForm({ ...payrollForm, employee_id: value });
                    calculatePayroll(value);
                  }}
                  disabled={!!editingPayroll}
                >
                  <SelectTrigger data-testid="payroll-employee-select">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.filter(e => e.is_active).map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.name} - {emp.position} ({formatCurrency(emp.basic_salary)}/mo)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Pay Period Start *</Label>
                <Input
                  type="date"
                  value={payrollForm.pay_period_start}
                  onChange={(e) => setPayrollForm({ ...payrollForm, pay_period_start: e.target.value })}
                  data-testid="payroll-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Pay Period End *</Label>
                <Input
                  type="date"
                  value={payrollForm.pay_period_end}
                  onChange={(e) => setPayrollForm({ ...payrollForm, pay_period_end: e.target.value })}
                  data-testid="payroll-end-date"
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <p className="text-sm font-medium text-green-600 mb-3">Earnings</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Days Worked</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={payrollForm.days_worked}
                    onChange={(e) => setPayrollForm({ ...payrollForm, days_worked: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Basic Pay (PHP) *</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={payrollForm.basic_pay}
                    onChange={(e) => setPayrollForm({ ...payrollForm, basic_pay: e.target.value })}
                    data-testid="payroll-basic-pay"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Overtime Hours</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={payrollForm.overtime_hours}
                    onChange={(e) => setPayrollForm({ ...payrollForm, overtime_hours: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Overtime Pay</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={payrollForm.overtime_pay}
                    onChange={(e) => setPayrollForm({ ...payrollForm, overtime_pay: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Allowances</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={payrollForm.allowances}
                    onChange={(e) => setPayrollForm({ ...payrollForm, allowances: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Bonuses</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={payrollForm.bonuses}
                    onChange={(e) => setPayrollForm({ ...payrollForm, bonuses: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <p className="text-sm font-medium text-red-600 mb-3">Deductions</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>SSS Contribution</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={payrollForm.sss_contribution}
                    onChange={(e) => setPayrollForm({ ...payrollForm, sss_contribution: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>PhilHealth</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={payrollForm.philhealth_contribution}
                    onChange={(e) => setPayrollForm({ ...payrollForm, philhealth_contribution: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Pag-IBIG</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={payrollForm.pagibig_contribution}
                    onChange={(e) => setPayrollForm({ ...payrollForm, pagibig_contribution: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tax Withheld</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={payrollForm.tax_withheld}
                    onChange={(e) => setPayrollForm({ ...payrollForm, tax_withheld: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Other Deductions</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={payrollForm.other_deductions}
                    onChange={(e) => setPayrollForm({ ...payrollForm, other_deductions: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Deduction Notes</Label>
                  <Input
                    placeholder="e.g., Cash advance"
                    value={payrollForm.deduction_notes}
                    onChange={(e) => setPayrollForm({ ...payrollForm, deduction_notes: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Additional notes"
                value={payrollForm.notes}
                onChange={(e) => setPayrollForm({ ...payrollForm, notes: e.target.value })}
              />
            </div>

            {/* Payroll Summary */}
            <div className="bg-secondary/50 rounded-lg p-4 mt-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-sm text-muted-foreground">Gross Pay</p>
                  <p className="text-lg font-bold text-blue-600">
                    {formatCurrency(
                      (parseFloat(payrollForm.basic_pay) || 0) +
                      (parseFloat(payrollForm.overtime_pay) || 0) +
                      (parseFloat(payrollForm.allowances) || 0) +
                      (parseFloat(payrollForm.bonuses) || 0)
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Deductions</p>
                  <p className="text-lg font-bold text-red-600">
                    {formatCurrency(
                      (parseFloat(payrollForm.sss_contribution) || 0) +
                      (parseFloat(payrollForm.philhealth_contribution) || 0) +
                      (parseFloat(payrollForm.pagibig_contribution) || 0) +
                      (parseFloat(payrollForm.tax_withheld) || 0) +
                      (parseFloat(payrollForm.other_deductions) || 0)
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Net Pay</p>
                  <p className="text-lg font-bold text-green-600">
                    {formatCurrency(
                      ((parseFloat(payrollForm.basic_pay) || 0) +
                      (parseFloat(payrollForm.overtime_pay) || 0) +
                      (parseFloat(payrollForm.allowances) || 0) +
                      (parseFloat(payrollForm.bonuses) || 0)) -
                      ((parseFloat(payrollForm.sss_contribution) || 0) +
                      (parseFloat(payrollForm.philhealth_contribution) || 0) +
                      (parseFloat(payrollForm.pagibig_contribution) || 0) +
                      (parseFloat(payrollForm.tax_withheld) || 0) +
                      (parseFloat(payrollForm.other_deductions) || 0))
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayrollModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddPayroll} data-testid="save-payroll-btn">
              {editingPayroll ? 'Update' : 'Create'} Payroll
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Employee Confirmation */}
      <Dialog open={!!deleteEmployeeConfirm} onOpenChange={() => setDeleteEmployeeConfirm(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Remove Employee
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this employee? They will be deactivated if they have payroll history.
            </DialogDescription>
          </DialogHeader>
          {deleteEmployeeConfirm && (
            <div className="py-4 px-4 bg-secondary/50 rounded-lg">
              <p className="font-medium">{deleteEmployeeConfirm.name}</p>
              <p className="text-sm text-muted-foreground">{deleteEmployeeConfirm.position} - {deleteEmployeeConfirm.department}</p>
              <p className="text-sm text-blue-600 mt-1">Salary: {formatCurrency(deleteEmployeeConfirm.basic_salary)}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteEmployeeConfirm(null)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => handleDeleteEmployee(deleteEmployeeConfirm?.id)}
              data-testid="confirm-delete-employee"
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Payroll Confirmation */}
      <Dialog open={!!deletePayrollConfirm} onOpenChange={() => setDeletePayrollConfirm(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Delete Payroll Record
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this payroll record? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {deletePayrollConfirm && (
            <div className="py-4 px-4 bg-secondary/50 rounded-lg">
              <p className="font-medium">{deletePayrollConfirm.employee_name}</p>
              <p className="text-sm text-muted-foreground">
                {formatDate(deletePayrollConfirm.pay_period_start)} - {formatDate(deletePayrollConfirm.pay_period_end)}
              </p>
              <p className="text-lg font-bold text-green-600 mt-2">Net Pay: {formatCurrency(deletePayrollConfirm.net_pay)}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletePayrollConfirm(null)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => handleDeletePayroll(deletePayrollConfirm?.id)}
              data-testid="confirm-delete-payroll"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payslip Modal */}
      <Dialog open={showPayslipModal} onOpenChange={setShowPayslipModal}>
        <DialogContent className="sm:max-w-3xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Employee Payslip
            </DialogTitle>
            <DialogDescription>
              Pay period: {formatDate(selectedPayslip?.pay_period_start)} - {formatDate(selectedPayslip?.pay_period_end)}
            </DialogDescription>
          </DialogHeader>
          
          {selectedPayslip && (
            <div ref={payslipRef} className="bg-white p-6 payslip-container">
              {/* Payslip Header */}
              <div className="header text-center border-b-2 border-gray-800 pb-4 mb-6">
                <h1 className="text-2xl font-bold text-gray-900">
                  {companySettings?.business_name || 'Company Name'}
                </h1>
                <p className="text-sm text-gray-600">{companySettings?.address || ''}</p>
                <p className="text-sm text-gray-600">{companySettings?.contact_number || ''}</p>
                <div className="mt-3 inline-block bg-blue-100 text-blue-800 px-4 py-1 rounded-full text-sm font-semibold">
                  PAYSLIP
                </div>
              </div>

              {/* Employee Information */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Employee Name:</span>
                    <span className="font-semibold">{selectedPayslip.employee_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Position:</span>
                    <span>{selectedPayslip.employee_details?.position || selectedPayslip.employee_position || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Department:</span>
                    <span>{selectedPayslip.employee_details?.department || '-'}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pay Period:</span>
                    <span className="font-semibold">{formatDate(selectedPayslip.pay_period_start)} - {formatDate(selectedPayslip.pay_period_end)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Days Worked:</span>
                    <span>{selectedPayslip.days_worked || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      selectedPayslip.status === 'Paid' ? 'bg-green-100 text-green-700' :
                      selectedPayslip.status === 'Approved' ? 'bg-blue-100 text-blue-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {selectedPayslip.status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Earnings Table */}
              <div className="mb-4">
                <div className="section-title bg-green-50 text-green-800 px-3 py-2 font-semibold rounded-t">
                  EARNINGS
                </div>
                <table className="w-full border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3 border-b">Description</th>
                      <th className="text-right p-3 border-b">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="p-3">Basic Pay</td>
                      <td className="p-3 text-right">{formatCurrency(selectedPayslip.basic_pay)}</td>
                    </tr>
                    {selectedPayslip.overtime_pay > 0 && (
                      <tr className="border-b">
                        <td className="p-3">Overtime Pay ({selectedPayslip.overtime_hours || 0} hrs)</td>
                        <td className="p-3 text-right">{formatCurrency(selectedPayslip.overtime_pay)}</td>
                      </tr>
                    )}
                    {selectedPayslip.allowances > 0 && (
                      <tr className="border-b">
                        <td className="p-3">Allowances</td>
                        <td className="p-3 text-right">{formatCurrency(selectedPayslip.allowances)}</td>
                      </tr>
                    )}
                    {selectedPayslip.bonuses > 0 && (
                      <tr className="border-b">
                        <td className="p-3">Bonuses</td>
                        <td className="p-3 text-right">{formatCurrency(selectedPayslip.bonuses)}</td>
                      </tr>
                    )}
                    <tr className="total-row bg-green-50 font-bold">
                      <td className="p-3">GROSS PAY</td>
                      <td className="p-3 text-right text-green-700">{formatCurrency(selectedPayslip.gross_pay)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Deductions Table */}
              <div className="mb-4">
                <div className="section-title bg-red-50 text-red-800 px-3 py-2 font-semibold rounded-t">
                  DEDUCTIONS
                </div>
                <table className="w-full border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3 border-b">Description</th>
                      <th className="text-right p-3 border-b">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPayslip.sss_contribution > 0 && (
                      <tr className="border-b">
                        <td className="p-3">SSS Contribution</td>
                        <td className="p-3 text-right">{formatCurrency(selectedPayslip.sss_contribution)}</td>
                      </tr>
                    )}
                    {selectedPayslip.philhealth_contribution > 0 && (
                      <tr className="border-b">
                        <td className="p-3">PhilHealth</td>
                        <td className="p-3 text-right">{formatCurrency(selectedPayslip.philhealth_contribution)}</td>
                      </tr>
                    )}
                    {selectedPayslip.pagibig_contribution > 0 && (
                      <tr className="border-b">
                        <td className="p-3">Pag-IBIG</td>
                        <td className="p-3 text-right">{formatCurrency(selectedPayslip.pagibig_contribution)}</td>
                      </tr>
                    )}
                    {selectedPayslip.tax_withheld > 0 && (
                      <tr className="border-b">
                        <td className="p-3">Withholding Tax</td>
                        <td className="p-3 text-right">{formatCurrency(selectedPayslip.tax_withheld)}</td>
                      </tr>
                    )}
                    {selectedPayslip.other_deductions > 0 && (
                      <tr className="border-b">
                        <td className="p-3">Other Deductions {selectedPayslip.deduction_notes && `(${selectedPayslip.deduction_notes})`}</td>
                        <td className="p-3 text-right">{formatCurrency(selectedPayslip.other_deductions)}</td>
                      </tr>
                    )}
                    <tr className="total-row bg-red-50 font-bold">
                      <td className="p-3">TOTAL DEDUCTIONS</td>
                      <td className="p-3 text-right text-red-700">{formatCurrency(selectedPayslip.total_deductions)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Net Pay */}
              <div className="net-pay bg-emerald-100 p-4 rounded-lg flex justify-between items-center">
                <span className="text-lg font-bold text-emerald-800">NET PAY</span>
                <span className="text-2xl font-bold text-emerald-700">{formatCurrency(selectedPayslip.net_pay)}</span>
              </div>

              {/* Notes */}
              {selectedPayslip.notes && (
                <div className="mt-4 p-3 bg-gray-50 rounded border">
                  <p className="text-sm text-gray-600"><strong>Notes:</strong> {selectedPayslip.notes}</p>
                </div>
              )}

              {/* Footer */}
              <div className="mt-6 pt-4 border-t text-center text-xs text-gray-500">
                <p>This is a computer-generated payslip. No signature required.</p>
                <p>Generated on {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowPayslipModal(false)}>
              Close
            </Button>
            <Button variant="outline" onClick={handlePrintPayslip}>
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
            <Button onClick={handleDownloadPayslip} data-testid="download-payslip-btn">
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
